<script setup lang="ts">
import { useUserStore } from "@/stores/user"
import { Field, useForm } from "vee-validate"
import { useI18n } from "vue-i18n"

const { t } = useI18n()
const userStore = useUserStore()
const runtimeConfig = useRuntimeConfig()

const initialValues = computed(() => ({
  email: "",
  password: "",
  trustDevice: false,
}))

const {
  handleSubmit,
  isSubmitting,
  setFieldError,
  meta,
  values,
  errors,
  resetForm,
  setFieldValue,
  setErrors,
} = useForm({
  initialValues,
})

const router = useRouter()
const toaster = useToaster()

// This is where you would send the form data to the server
const onSubmit = handleSubmit(async (values) => {
  // here you have access to the validated form values

  const { data, error } = await useCustomFetch("/api/login", {
    method: "post",
    body: values,
  })

  if (data.value?.success) {
    if (data.value?.message === "TWO_FACTOR_REQUIRED") {
      router.push("/two-factor")
      userStore.setTwoFactor(data.value.data.two_factor_token)
      userStore.setUser({
        email: values?.email,
      })
      return
    }
    userStore.setToken(data.value.data.access_token)
    router.push("/home")
    toaster.show({
      title: t("success"),
      message: data?.value?.message,
      color: "success",
      icon: "ph:check",
      closable: true,
    })
  } else {
    toaster.show({
      title: t("error"),
      message: data?.value?.message,
      color: "danger",
      icon: "lucide:alert-triangle",
      closable: true,
    })
  }
})

function openForgotPage() {
  router.push({ path: "/forgot" })
}

onMounted(() => {
  // getActivePinia()._s.forEach((store) => store.$reset())
})

const handleIndexPath = () => {
  if (process.client) {
    window.location.pathname = "/"
  }
}

const contracts = ref([
  { name: "KVKK Politikası", path: "/pages/Gigcep-KVKK-Politikasi" },
  {
    name: "KVKK Aydınlatma Metni",
    path: "/pages/Gigcep-KVKK-Aydinlatma-Metni",
  },
  {
    name: "Kullanıcı Sözleşmesi",
    path: "/pages/Gigcep-Kullanici-Sozlesmesi",
  },
  { name: "Gizlilik Politikası", path: "/pages/Gigcep-Gizlilik-Politikasi" },
  { name: "Çerez Politikası", path: "/pages/Gigcep-Cerez-Politikasi" },
  { name: "Açık Rıza Metni", path: "/pages/Gigcep-Acik-Riza-Metni" },
])
</script>

<template>
  <div class="flex h-screen w-full flex-col items-center md:flex-row">
    <div class="bg-muted-100 hidden h-screen w-full md:w-1/2 lg:block xl:w-2/3">
      <div
        class="mx-auto w-full h-full flex items-center justify-center max-w-4xl"
      >
        <img
          class="max-w-xl mx-auto"
          src="/img/login/gig.png"
          alt=""
          width="1200"
          height="996"
          v-if="runtimeConfig.public.source == 'user'"
        />
        <img
          class="max-w-xl mx-auto"
          src="/img/login/social.svg"
          alt=""
          width="1000"
          height="1000"
          v-else
        />
      </div>
    </div>

    <div
      class="flex h-screen w-full items-center justify-center bg-white px-6 md:mx-auto md:w-1/2 md:max-w-md lg:max-w-full xl:w-1/3"
    >
      <div
        class="mx-auto flex h-full w-full max-w-md flex-col items-center justify-between py-2 md:py-4"
      >
        <div class="mx-auto flex w-full max-w-xs items-center justify-between">
          <NuxtLink
            @click="handleIndexPath"
            class="text-muted-400 hover:text-primary-500 transition-colors duration-300 cursor-pointer"
          >
            <GlobalLogo class="h-10 w-30" />
          </NuxtLink>
          <div></div>
        </div>
        <div class="w-full mt-3 lg:px-16 xl:px-12">
          <BaseHeading as="h2" size="3xl" weight="medium">
            {{ $t("welcome_back") }}!
          </BaseHeading>
          <BaseParagraph size="sm" class="text-muted-400 mb-6">
            {{ $t("enter_your_account_credentials") }}
          </BaseParagraph>

          <form
            method="POST"
            action=""
            @submit.prevent="onSubmit"
            class="mt-6"
            novalidate
          >
            <div class="space-y-4">
              <Field
                v-slot="{ field, errorMessage, handleChange, handleBlur }"
                name="email"
                rules="required|email"
              >
                <BaseInput
                  :model-value="field.value"
                  :error="errorMessage"
                  :disabled="isSubmitting"
                  type="email"
                  :label="$t('email')"
                  icon="ph:user-duotone"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  @keyup.enter="onSubmit"
                />
              </Field>
              <Field
                v-slot="{ field, errorMessage, handleChange, handleBlur }"
                name="password"
                rules="required"
              >
                <BaseInput
                  :model-value="field.value"
                  :error="errorMessage"
                  :disabled="isSubmitting"
                  type="password"
                  :label="$t('password')"
                  icon="ph:lock-duotone"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  @keyup.enter="onSubmit"
                />
              </Field>
            </div>

            
            <div class="mt-6 flex items-center justify-between">
              <Field
                v-slot="{ field, errorMessage, handleChange, handleBlur }"
                name="trustDevice"
              >
                <BaseCheckbox
                  :model-value="field.value"
                  :disabled="isSubmitting"
                  shape="rounded"
                  :label="$t('remember_this_account')"
                  color="primary"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  @keyup.enter="onSubmit"
                />
              </Field>
            </div>

            <div class="mt-6">
              <div
                class="flex flex-col xl:flex-row block w-full rounded-md shadow-sm gap-2"
              >
                <BaseButton
                  @click="onSubmit"
                  :disabled="isSubmitting"
                  :loading="isSubmitting"
                  shape="rounded"
                  color="primary"
                  class="w-full mr-2"
                >
                  {{ $t("login") }}
                </BaseButton>
                <BaseButton
                  @click="openForgotPage"
                  :disabled="isSubmitting"
                  shape="rounded"
                  color="info"
                  class="w-full text-nowrap"
                >
                  {{ $t("forgot_my_password") }}
                </BaseButton>
              </div>
            </div>
          </form>
          <p
            class="text-muted-400 mt-2 flex justify-end font-sans text-xs leading-5"
          >
            <NuxtLink
              to="/register"
              class="text-primary-600 hover:text-primary-500 font-medium underline-offset-4 transition duration-150 ease-in-out hover:underline"
            >
              {{ $t("create_account") }}
            </NuxtLink>
          </p>
        </div>
        
        <div class="grid grid-cols-2">
          <div class="col-span-2 text-center">
            <span class="text-sm font-bold text-slate-900">
              {{ $t("contracts") }}
            </span>
          </div>
          <template v-for="(item, index) in contracts">
            <NuxtLink target="_blank" :to="item.path">
              <span
                class="text-xs text-slate-500 hover:text-orange-500 transition-all duration-200 cursor-pointer"
              >
                {{ item.name }}
              </span>
            </NuxtLink>
          </template>
        </div>
        <div class="text-center">
          <BaseText size="xs" class="text-muted-400">
            © {{ new Date().getFullYear() }}
            {{
              runtimeConfig.public.source == "user"
                ? $t("gigcep")
                : $t("sosyal360")
            }}
            {{ $t("all_rights_reserved") }}.
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>
